<template>
  <div class="patient-group">
    <div
      class="patient-group__header"
      role="button"
      tabindex="0"
      @click="handleToggle"
      @keydown.enter="handleToggle"
      @keydown.space.prevent="handleToggle"
    >
      <div class="patient-group__title-section">
        <h3 class="patient-group__title">{{ group.title }} ({{ group.count }})</h3>
      </div>
      <div class="patient-group__info-section">
        <span class="patient-group__target-response">{{ group.targetResponse }}</span>
        <v-btn icon variant="text" size="small" :aria-label="group.isExpanded ? 'Collapse group' : 'Expand group'">
          <v-icon>{{ group.isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
        </v-btn>
      </div>
    </div>

    <v-expand-transition>
      <div v-show="group.isExpanded" class="patient-group__content">
        <patient-row
          v-for="patient in group.patients"
          :key="patient.id"
          :patient="patient"
          @call="handleCall"
          @breakthrough="handleBreakthrough"
          @expand="handleRowExpand"
        />
      </div>
    </v-expand-transition>
  </div>
</template>

<script lang="ts" setup>
import type { PatientGroup } from "@virtual-care/shared";
import PatientRow from "./PatientRow.vue";

interface Props {
  group: PatientGroup;
}

interface Emits {
  (e: "toggle", groupId: string): void;
  (e: "call", patientId: string): void;
  (e: "breakthrough", patientId: string): void;
  (e: "expand-row", patientId: string): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const handleToggle = () => {
  emit("toggle", props.group.id);
};

const handleCall = (patientId: string) => {
  emit("call", patientId);
};

const handleBreakthrough = (patientId: string) => {
  emit("breakthrough", patientId);
};

const handleRowExpand = (patientId: string) => {
  emit("expand-row", patientId);
};
</script>

<style lang="scss" scoped>
.patient-group {
  width: 100%;
  margin-bottom: 1px;
}

.patient-group__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  min-height: 44px;
  cursor: pointer;
  background: #f6f5fb;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(246, 245, 251, 0.8);
  }
}

.patient-group__title-section {
  display: flex;
  align-items: center;
}

.patient-group__title {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.428em;
  color: #222222;
  margin: 0;
}

.patient-group__info-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.patient-group__target-response {
  font-family: "Proxima Nova", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.333em;
  color: #6a5d8a;
}

.patient-group__content {
  width: 100%;
  background: #ffffff;
}

@media (max-width: 767px) {
  .patient-group__header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 16px;
  }

  .patient-group__info-section {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
